// -------------------------------------------
// FormBuilderTab.js
// -------------------------------------------
import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  IconButton,
  Box,
  Divider,
  MenuItem,
  CircularProgress,
  Paper,
  Tooltip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  Checkbox,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/AddCircle";
import SaveIcon from "@mui/icons-material/Save";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PreviewIcon from "@mui/icons-material/Visibility";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import api from "../../utils/api";
import ElectionDocumentsViewer from "./ElectionDocumentsViewer";

const QUESTION_TYPES = [
  { label: "Single Choice", value: "single" },
  { label: "Multiple Choice", value: "multiple" },
  { label: "Yes / No", value: "yesno" },
  { label: "Text Answer", value: "text" },
];

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

/* -----------------------------------------------------
   SORTABLE WRAPPER
----------------------------------------------------- */
const SortableQuestion = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {React.cloneElement(children, {
        dragHandleProps: { ...attributes, ...listeners },
      })}
    </div>
  );
};

/* -----------------------------------------------------
   QUESTION CARD COMPONENT
----------------------------------------------------- */
const QuestionCard = ({
  q,
  qIndex,
  dragHandleProps,
  onDeleteQuestion,
  onUpdateQuestion,
  onUpdateOption,
  onDeleteOption,
  onAddOption,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, md: 2.5 },
      mb: 3,
      borderRadius: "16px",
      border: "1px solid rgba(47,99,173,0.08)",
      boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
      backgroundColor: "rgba(255,255,255,0.98)",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        mb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton
          size="small"
          sx={{
            cursor: "grab",
            borderRadius: "10px",
            color: "#6b778c",
            backgroundColor: "rgba(47,99,173,0.04)",
            "&:hover": {
              backgroundColor: "rgba(47,99,173,0.08)",
            },
          }}
          {...dragHandleProps}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>

        <Box>
          <Typography sx={{ fontWeight: 700, color: "#223142" }}>
            Question {qIndex + 1}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Configure the question text, answer type, and options.
          </Typography>
        </Box>
      </Box>

      <Tooltip title="Delete Question">
        <IconButton
          color="error"
          onClick={onDeleteQuestion}
          sx={{
            borderRadius: "10px",
            backgroundColor: "rgba(239,68,68,0.06)",
            "&:hover": {
              backgroundColor: "rgba(239,68,68,0.12)",
            },
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </Box>

    <TextField
      fullWidth
      label="Question Text"
      value={q.question_text}
      onChange={(e) => onUpdateQuestion("question_text", e.target.value)}
      sx={{
        mb: 2,
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
        },
      }}
    />

    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        alignItems: { md: "center" },
      }}
    >
      <TextField
        select
        fullWidth
        label="Question Type"
        value={q.question_type}
        onChange={(e) => onUpdateQuestion("question_type", e.target.value)}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
          },
        }}
      >
        {QUESTION_TYPES.map((t) => (
          <MenuItem key={t.value} value={t.value}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>

      <FormControlLabel
        control={
          <Switch
            checked={!!q.is_required}
            onChange={(e) => onUpdateQuestion("is_required", e.target.checked)}
          />
        }
        label="Required"
      />
    </Box>

    {(q.question_type === "single" || q.question_type === "multiple") && (
      <>
        <Divider sx={{ my: 2.5 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
            gap: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: "#223142" }}>
            Options
          </Typography>

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddOption}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
            }}
          >
            Add Option
          </Button>
        </Box>

        {q.options.map((opt, oIndex) => (
          <Box
            key={opt.id}
            sx={{
              display: "flex",
              gap: 1.5,
              mb: 1.25,
              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              label={`Option ${oIndex + 1}`}
              value={opt.option_text}
              onChange={(e) => onUpdateOption(oIndex, e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />

            <IconButton
              color="error"
              onClick={() => onDeleteOption(oIndex)}
              sx={{
                borderRadius: "10px",
                backgroundColor: "rgba(239,68,68,0.06)",
                "&:hover": {
                  backgroundColor: "rgba(239,68,68,0.12)",
                },
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
      </>
    )}
  </Paper>
);

/* -----------------------------------------------------
   MAIN COMPONENT
----------------------------------------------------- */
const FormBuilderTab = ({ election }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocs, setPreviewDocs] = useState([]);
  const [deleteQIndex, setDeleteQIndex] = useState(null); // index pending delete confirmation, or null
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const sensors = useSensors(useSensor(PointerSensor));

  const loadQuestions = async () => {
    if (!election?.id) return;
    setLoading(true);

    try {
      const res = await api.get(`/elections/${election.id}/questions`);
      setQuestions(
        res.data.map((q, index) => ({
          ...q,
          is_required: q.is_required === 1,
          sort_order: q.sort_order ?? index,
          options: q.options || [],
        }))
      );
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadQuestions();
  }, [election?.id]);

  // Fetch documents each time the preview opens so just-uploaded files show up,
  // matching what voters see on the real ballot.
  useEffect(() => {
    if (!previewOpen || !election?.id) return;
    api.get(`/elections/${election.id}/documents`)
      .then((res) => setPreviewDocs(res.data || []))
      .catch((err) => console.error("Failed to load election documents:", err));
  }, [previewOpen, election?.id]);

  const addQuestion = async () => {
    const payload = {
      question_text: "",
      question_type: "single",
      is_required: 0,
      sort_order: questions.length,
    };

    try {
      const res = await api.post(`/elections/${election.id}/questions`, payload);
      setQuestions((prev) => [...prev, { ...res.data, options: [] }]);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteQuestion = (qIndex) => setDeleteQIndex(qIndex);

  const confirmDeleteQuestion = async () => {
    const qIndex = deleteQIndex;
    setDeleteQIndex(null);
    if (qIndex === null) return;
    const q = questions[qIndex];

    try {
      await api.delete(`/questions/${q.id}`);
      setQuestions((prev) => prev.filter((_, i) => i !== qIndex));
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: "Failed to delete question.", severity: "error" });
    }
  };

  const addOption = async (qIndex) => {
    const q = questions[qIndex];

    try {
      const res = await api.post(`/questions/${q.id}/options`, {
        option_text: "",
        sort_order: q.options.length,
      });

      setQuestions((prev) => {
        const copy = [...prev];
        copy[qIndex].options.push(res.data);
        return copy;
      });
    } catch (err) {
      console.error(err);
    }
  };

  const deleteOption = async (qIndex, oIndex) => {
    const opt = questions[qIndex].options[oIndex];

    try {
      await api.delete(`/options/${opt.id}`);

      setQuestions((prev) => {
        const copy = [...prev];
        copy[qIndex].options.splice(oIndex, 1);
        return copy;
      });
    } catch (err) {
      console.error(err);
    }
  };

  const updateQuestionField = (qIndex, field, value) => {
    setQuestions((prev) => {
      const copy = [...prev];
      copy[qIndex][field] = value;
      return copy;
    });
  };

  const updateOptionField = (qIndex, oIndex, value) => {
    setQuestions((prev) => {
      const copy = [...prev];
      copy[qIndex].options[oIndex].option_text = value;
      return copy;
    });
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    setQuestions((prev) => {
      const oldIndex = prev.findIndex((x) => x.id === active.id);
      const newIndex = prev.findIndex((x) => x.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const saveBallot = async () => {
    try {
      for (const [i, q] of questions.entries()) {
        await api.put(`/questions/${q.id}`, {
          question_text: q.question_text,
          question_type: q.question_type,
          is_required: q.is_required ? 1 : 0,
          sort_order: i,
        });

        for (const [j, opt] of q.options.entries()) {
          await api.put(`/options/${opt.id}`, {
            option_text: opt.option_text,
            sort_order: j,
          });
        }
      }

      setSnack({ open: true, message: "Ballot saved!", severity: "success" });
      loadQuestions();
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: "Failed to save ballot.", severity: "error" });
    }
  };

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          p: 4,
          textAlign: "center",
        }}
      >
        <CircularProgress />
      </Card>
    );
  }

  return (
    <>
      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          p: { xs: 2, md: 3 },
        }}
      >
        <CardContent sx={{ p: "0 !important" }}>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
              >
                Ballot Form Builder
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Build and organize the election ballot questions and answer options.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => setPreviewOpen(true)}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Preview
              </Button>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addQuestion}
                sx={{
                  backgroundColor: "#2f63ad",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#275592",
                    boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                  },
                }}
              >
                Add Question
              </Button>
            </Stack>
          </Box>

          {questions.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: "center",
                borderRadius: "16px",
                border: "1px dashed rgba(47,99,173,0.14)",
                backgroundColor: "rgba(47,99,173,0.02)",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#223142", mb: 1 }}
              >
                No questions yet
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                Start building your ballot by adding the first question.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addQuestion}
                sx={{
                  backgroundColor: "#2f63ad",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "none",
                }}
              >
                Add First Question
              </Button>
            </Paper>
          ) : (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={questions.map((q) => q.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {questions.map((q, qIndex) => (
                    <SortableQuestion key={q.id} id={q.id}>
                      <QuestionCard
                        q={q}
                        qIndex={qIndex}
                        dragHandleProps
                        onDeleteQuestion={() => deleteQuestion(qIndex)}
                        onUpdateQuestion={(field, value) =>
                          updateQuestionField(qIndex, field, value)
                        }
                        onUpdateOption={(oIndex, value) =>
                          updateOptionField(qIndex, oIndex, value)
                        }
                        onDeleteOption={(oIndex) => deleteOption(qIndex, oIndex)}
                        onAddOption={() => addOption(qIndex)}
                      />
                    </SortableQuestion>
                  ))}
                </SortableContext>
              </DndContext>

              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                sx={{
                  mt: 1,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                }}
                onClick={saveBallot}
              >
                Save Ballot
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            boxShadow: "0 18px 60px rgba(0,0,0,0.16)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
          Ballot Preview
        </DialogTitle>

        <DialogContent dividers>
          <ElectionDocumentsViewer documents={previewDocs} />

          {questions.length === 0 && (
            <Typography color="text.secondary">
              No questions yet. Add questions to preview the ballot.
            </Typography>
          )}

          {questions.map((q, index) => (
            <Box key={q.id} sx={{ mb: 3 }}>
              <Typography
                fontWeight="bold"
                sx={{ mb: 1.25, color: "#223142" }}
              >
                {index + 1}. {q.question_text || "Untitled question"}
                {q.is_required && (
                  <Typography
                    component="span"
                    sx={{ color: "error.main", ml: 0.5 }}
                  >
                    *
                  </Typography>
                )}
              </Typography>

              <Box sx={{ pl: 1 }}>
                {(() => {
                  switch (q.question_type) {
                    case "single":
                      return (
                        <RadioGroup>
                          {q.options.map((opt) => (
                            <FormControlLabel
                              key={opt.id}
                              value={opt.id}
                              control={<Radio />}
                              label={opt.option_text || "Option"}
                            />
                          ))}
                        </RadioGroup>
                      );

                    case "multiple":
                      return (
                        <Box>
                          {q.options.map((opt) => (
                            <Box
                              key={opt.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 0.5,
                              }}
                            >
                              <Checkbox />
                              <Typography>{opt.option_text || "Option"}</Typography>
                            </Box>
                          ))}
                        </Box>
                      );

                    case "yesno":
                      return (
                        <RadioGroup row>
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      );

                    case "text":
                    default:
                      return (
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Enter your answer..."
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                            },
                          }}
                        />
                      );
                  }
                })()}
              </Box>

              <Divider sx={{ mt: 2.5 }} />
            </Box>
          ))}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setPreviewOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteQIndex !== null}
        onClose={() => setDeleteQIndex(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#223142" }}>
          Delete Question?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#5a6578" }}>
            This will permanently remove the question and its options. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteQIndex(null)}
            sx={{ textTransform: "none", fontWeight: 600, color: "#5a6578" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteQuestion}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, boxShadow: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((p) => ({ ...p, open: false }))}
          sx={{ borderRadius: "12px" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FormBuilderTab;